import { Card, CardContent } from "@repo/ui";
import { Link } from "@tanstack/react-router";
import { Package, TrendingDown, TrendingUp, CalendarClock } from "lucide-react";

interface KpiCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  iconBg: string;
  href?: string;
}

function KpiCard({ label, value, icon, iconBg, href }: KpiCardProps) {
  const content = (
    <Card className={href ? "transition-colors hover:border-primary/30" : ""}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="text-2xl font-semibold tracking-tight mt-1 tabular-nums">
              {value}
            </p>
          </div>
          <div className={`rounded-lg p-2.5 ${iconBg}`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return content;
}

interface DashboardKpiStatsProps {
  totalItems: number | null;
  todayDispenses: number;
  todayReceipts: number;
  remindersDue: number;
}

export function DashboardKpiStats({
  totalItems,
  todayDispenses,
  todayReceipts,
  remindersDue,
}: DashboardKpiStatsProps) {
  return (
    <div
      data-tour="kpi-stats"
      className="grid grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <KpiCard
        label="Total Items"
        value={totalItems ?? 0}
        icon={<Package className="h-5 w-5 text-muted-foreground" />}
        iconBg="bg-muted"
        href="/inventory"
      />
      <KpiCard
        label="Today's Dispenses"
        value={todayDispenses}
        icon={<TrendingDown className="h-5 w-5 text-primary" />}
        iconBg="bg-primary-soft"
        href="/dispenses"
      />
      <KpiCard
        label="Today's Receipts"
        value={todayReceipts}
        icon={<TrendingUp className="h-5 w-5 text-success" />}
        iconBg="bg-success-soft"
        href="/receipts"
      />
      <KpiCard
        label="Reminders Due"
        value={remindersDue}
        icon={<CalendarClock className="h-5 w-5 text-warning" />}
        iconBg="bg-warning-soft"
        href="/reminders"
      />
    </div>
  );
}
